import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { Injectable, Type } from "@angular/core";
import { ReplicaLookupService } from "./ReplicaLookupService";
import { PromiseBlotterLookupService } from "./PromiseBlotterLookupService";

import {
  PRIMITIVE,
  ClientMessageBrokerFactory,
  ClientMessageBroker,
  FnArg,
  FnTransferableArg,
  UiArguments,
} from "shared/provider-webworker";
import { IPromiseBlotterLookupService } from "../IPromiseBlotterLookupService";
import { Subject } from "rxjs";

import { LookupEventType } from "../../model/core/LookupEventType";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);
export abstract class ReplicaBlotterLookupService<K, V extends CubiqTS.ConcreteEncodable>
  extends ReplicaLookupService<K, V>
  implements PromiseBlotterLookupService<K, V>, IPromiseBlotterLookupService<K, V>
{
  protected clientRefreshPort: MessagePort = null;
  protected modelDeleteEventSubject: Subject<K> = new Subject<K>();
  protected modelDeleteWithReasonEventSubject: Subject<K> = new Subject<K>();
  protected modelErrorEventSubject: Subject<any> = new Subject<any>();
  protected modelOrderEntryEventSubject: Subject<any> = new Subject<any>();
  protected orderEntryClearEventSubject: Subject<any> = new Subject<any>();
  protected modelNotificationEventSubject: Subject<CubiqTS.ConcreteEncodable> =
    new Subject<CubiqTS.ConcreteEncodable>();
  protected modelAppNotificationEventSubject: Subject<CubiqTS.ConcreteEncodable> =
    new Subject<CubiqTS.ConcreteEncodable>();
  protected modelBatchUpdateSubject: Subject<any> = new Subject<any>();
  protected modelDetailFillEventSubject: Subject<any> = new Subject<any>();
  // protected notifTypes:Set<BFXNotificationType> = new Set();
  // protected appNotifTypes:Set<BFXAppNotificationType> = new Set();
  protected detailFillIds: Map<string, string> = new Map();

  constructor(
    factory: ClientMessageBrokerFactory,
    lookupType: Type<CubiqTS.ConcreteEncodable>,
    serviceChannel: string,
    modelUpdateEventType: Type<CubiqTS.ConcreteEncodable> = CubiqTS.ModelUpdateImpl,
    autoSubscribeUpdates: boolean = true,
  ) {
    super(factory, lookupType, serviceChannel, modelUpdateEventType, autoSubscribeUpdates);
  }

  getModelDeleteSubject(): Subject<K> {
    return this.modelDeleteEventSubject;
  }

  getModelDeleteWithReasonSubject(): Subject<K> {
    return this.modelDeleteWithReasonEventSubject;
  }

  getModelErrorSubject(): Subject<any> {
    return this.modelErrorEventSubject;
  }

  getModelBatchUpdateSubject(): Subject<any> {
    return this.modelBatchUpdateSubject;
  }

  getModelOrderEntrySubject(): Subject<any> {
    return this.modelOrderEntryEventSubject;
  }

  getOrderEntryClearEventSubject(): Subject<any> {
    return this.orderEntryClearEventSubject;
  }

  startUpdates(): void {
    this.initModelUpdateService();
  }

  static counter: number = 0;

  protected initModelUpdateService(): void {
    if (this.clientRefreshPort == null) {
      let refreshChannel = new MessageChannel();
      this.clientRefreshPort = refreshChannel.port1;
      let serviceRefreshPort: MessagePort = refreshChannel.port2;

      let request = new UiArguments("initModelUpdateService", [
        new FnTransferableArg(serviceRefreshPort, PRIMITIVE),
      ]);

      this.clientRefreshPort.addEventListener("message", (event: any) => {
        switch (event.data.modelEventType) {
          case LookupEventType.DELETE:
            this.modelDeleteEventSubject.next(event.data.payload);
            break;
          case LookupEventType.DELETE_WITH_REASON:
            this.modelDeleteWithReasonEventSubject.next(event.data.payload);
            break;
          case LookupEventType.UPDATE:
            // will need to inspect payload to determine if also needs to be sent on notification topic
            event.data.payload.forEach((order) => {
              // this.checkForNotification(order);
            });

            this.modelUpdateEventSubject.next(event.data.payload);
            break;
          case LookupEventType.BATCH_UPDATE:
            this.modelBatchUpdateSubject.next(event.data.payload);
            break;
          case LookupEventType.NOTIFICATION:
            // this.checkForNotification(event.data.payload);
            break;
          case LookupEventType.ERROR:
            this.modelErrorEventSubject.next(event.data.payload);
            break;
          case LookupEventType.ORDER_ENTRY:
            this.modelOrderEntryEventSubject.next(event.data.payload);
            break;
          case LookupEventType.ORDER_ENTRY_CLEAR:
            this.orderEntryClearEventSubject.next(event.data.payload);
            break;
          case LookupEventType.DETAIL_FILL:
            if (event.data.payload instanceof Array) {
              event.data.payload.forEach((element) => {
                let orderId = element.id;
                if (this.detailFillIds.has(orderId)) {
                  this.detailFillIds.delete(orderId);
                  this.modelDetailFillEventSubject.next(element);
                }
              });
            } else {
              let orderId = event.data.payload.id;
              if (this.detailFillIds.has(orderId)) {
                this.detailFillIds.delete(orderId);
                this.modelDetailFillEventSubject.next(event.data.payload);
              }
            }
            break;
        }
      });

      this.broker.runOnService(request, null);
      this.clientRefreshPort.start();
    }
  }

  // setNotificationTypes(types:Array<BFXNotificationType>):void{
  //     // this.notifTypes.clear();
  //     // this.notifTypes = new Set(types);
  //     let request = new UiArguments('setNotificationTypes');
  //     let arg1 = new FnArg(types, PRIMITIVE);
  //     request.args = [arg1];
  //     this.broker.runOnService(request, null);
  // }

  // setAppNotificationTypes(types:Array<BFXAppNotificationType>):void{
  //     // this.appNotifTypes.clear();
  //     // this.appNotifTypes=new Set(types);
  //     let request = new UiArguments('setAppNotificationTypes');
  //     let arg1 = new FnArg(types, PRIMITIVE);
  //     request.args = [arg1];
  //     this.broker.runOnService(request, null);
  // }

  setDetailFillIds(orderIdRefs: Array<[string, string]>): void {
    this.detailFillIds.clear();
    this.detailFillIds = new Map(orderIdRefs);
    let request = new UiArguments("setDetailFillIds");
    let arg1 = new FnArg(orderIdRefs, PRIMITIVE);
    request.args = [arg1];
    this.broker.runOnService(request, null);
  }

  getModelNotificationSubject(): Subject<CubiqTS.ConcreteEncodable> {
    return this.modelNotificationEventSubject;
  }

  getModelAppNotificationSubject(): Subject<CubiqTS.ConcreteEncodable> {
    return this.modelAppNotificationEventSubject;
  }

  getModelDetailFillSubject(): Subject<any> {
    return this.modelDetailFillEventSubject;
  }

  setExtendedLogging(logState: boolean): void {
    let request = new UiArguments("setExtendedLogging");
    let arg1 = new FnArg(logState, PRIMITIVE);
    request.args = [arg1];
    this.broker.runOnService(request, null);
  }
}
